import { Component } from '@theme/component';

/** Matches a Searchspring hash segment, e.g. `filter:ss_size:S`. */
const FILTER_SEGMENT = /^filter:([^:]+):(.*)$/;

/**
 * Decodes a hash segment part, falling back to the raw text on malformed escapes.
 *
 * Athos escapes its own `/` separator by encoding twice and swapping % for $, so
 * `7/8` reads as `7$252F8` from its facets and `7%2F8` from the pills here. One
 * decode only unwraps the first layer, hence the second pass.
 */
function decodeSegmentPart(part) {
  let decoded = part.replace(/\$/g, '%');

  for (let pass = 0; pass < 2; pass++) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }

  return decoded;
}

/** Returns the `/`-separated segments of the current Searchspring hash. */
function readHashSegments() {
  return window.location.hash.replace(/^#\/?/, '').split('/').filter(Boolean);
}

/** Builds a `filter:<field>:<value>` segment. */
function buildFilterSegment(paramName, value) {
  return `filter:${encodeURIComponent(paramName)}:${encodeURIComponent(value)}`;
}

/** Parses a hash segment into its facet field and value, or null when it isn't a filter. */
function parseFilterSegment(segment) {
  const match = FILTER_SEGMENT.exec(segment);
  if (!match) return null;

  return { field: decodeSegmentPart(match[1]), value: decodeSegmentPart(match[2]) };
}

/** Fired after `pushState` or `replaceState`, neither of which has a native event. */
const LOCATION_CHANGE_EVENT = 'quick-filters:locationchange';

let isHistoryPatched = false;

/**
 * Makes pushState and replaceState announce themselves on window.
 *
 * Athos writes its filter state with pushState, which fires neither hashchange
 * nor popstate, so removing a filter in its own UI is otherwise unobservable.
 */
function patchHistoryEvents() {
  if (isHistoryPatched) return;
  isHistoryPatched = true;

  for (const method of ['pushState', 'replaceState']) {
    const original = history[method];

    history[method] = function patchedHistoryMethod(...args) {
      const result = original.apply(this, args);
      window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
      return result;
    };
  }
}

class QuickFiltersComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);

    if (this.#usesSearchspring) {
      patchHistoryEvents();
      window.addEventListener('hashchange', this.#syncFromHash);
      window.addEventListener('popstate', this.#syncFromHash);
      window.addEventListener(LOCATION_CHANGE_EVENT, this.#syncFromHash);
      this.#syncFromHash();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('hashchange', this.#syncFromHash);
    window.removeEventListener('popstate', this.#syncFromHash);
    window.removeEventListener(LOCATION_CHANGE_EVENT, this.#syncFromHash);
  }

  get #usesSearchspring() {
    return this.hasAttribute('data-ss-filters');
  }

  #getFacetsComponents(sectionId) {
    return Array.from(document.querySelectorAll(`facets-form-component[section-id="${sectionId}"]`));
  }

  #getMatchingInputs(facetsComponents, paramName, value) {
    return facetsComponents.flatMap((facetsComponent) => {
      return Array.from(facetsComponent.querySelectorAll('input[type="checkbox"]')).filter((input) => {
        return input instanceof HTMLInputElement && input.name === paramName && input.value === value;
      });
    });
  }

  #updateFiltersByURL(facetsComponents, paramName, value, shouldActivate) {
    const url = new URL(window.location.href);
    const nextValues = url.searchParams.getAll(paramName).filter((entryValue) => entryValue !== value);

    if (shouldActivate) {
      nextValues.push(value);
    }

    url.searchParams.delete(paramName);
    url.searchParams.delete('page');

    [...new Set(nextValues)].forEach((entryValue) => {
      url.searchParams.append(paramName, entryValue);
    });

    const facetsComponent = facetsComponents.find((component) => {
      return typeof component.updateFiltersByURL === 'function';
    });

    if (facetsComponent) {
      facetsComponent.updateFiltersByURL(url.toString());
      return;
    }

    window.location.assign(url.toString());
  }

  /** Toggles a `filter:<field>:<value>` segment in the hash Searchspring reads. */
  #updateHashFilters(paramName, value, shouldActivate) {
    const target = buildFilterSegment(paramName, value);
    const segments = readHashSegments().filter((segment) => {
      // Drop the toggled facet by value, and reset pagination on every change.
      if (segment.startsWith('page:')) return false;

      const parsed = parseFilterSegment(segment);
      if (!parsed) return true;

      return parsed.field !== paramName || parsed.value !== value;
    });

    if (shouldActivate) {
      segments.push(target);
    }

    // `#/` is Searchspring's own unfiltered route. Clearing to the bare pathname
    // instead leaves its results filtered: it never sees the change.
    window.location.hash = segments.length ? `#/${segments.join('/')}` : '#/';

    window.dispatchEvent(new HashChangeEvent('hashchange'));
    this.#syncFromHash();
  }

  /** Mirrors the pills' pressed state onto the facets currently in the hash. */
  #syncFromHash = () => {
    const activeFilters = readHashSegments()
      .map(parseFilterSegment)
      .filter((filter) => filter != null);

    this.querySelectorAll('[data-quick-filter-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const isActive = activeFilters.some((filter) => {
        return filter.field === button.dataset.paramName && filter.value === button.dataset.value;
      });

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        `${isActive ? 'Remove' : 'Apply'} ${button.dataset.filterLabel ?? button.dataset.value} filter`,
      );
    });
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-quick-filter-button]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();

    const sectionId = this.dataset.sectionId;
    const paramName = button.dataset.paramName;
    const value = button.dataset.value;

    if (!paramName || value == null) return;

    const shouldActivate = button.getAttribute('aria-pressed') !== 'true';

    if (this.#usesSearchspring) {
      this.#updateHashFilters(paramName, value, shouldActivate);
      return;
    }

    if (!sectionId) return;

    const facetsComponents = this.#getFacetsComponents(sectionId);

    if (!facetsComponents.length) {
      this.#updateFiltersByURL([], paramName, value, shouldActivate);
      return;
    }

    const matchingInputs = this.#getMatchingInputs(facetsComponents, paramName, value);

    if (!matchingInputs.length) {
      this.#updateFiltersByURL(facetsComponents, paramName, value, shouldActivate);
      return;
    }

    matchingInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.disabled && shouldActivate) return;
      input.checked = shouldActivate;
    });

    const facetsComponent = facetsComponents.find((component) => {
      return typeof component.updateFilters === 'function';
    });

    if (!facetsComponent) {
      this.#updateFiltersByURL(facetsComponents, paramName, value, shouldActivate);
      return;
    }

    facetsComponent.updateFilters();
  };
}

if (!customElements.get('quick-filters-component')) {
  customElements.define('quick-filters-component', QuickFiltersComponent);
}
